import { API_ROOT } from 'src/constants';
import Request, { setData, setMethod, setURL } from 'src/services';
import { getNamespaceName } from 'src/services/objects/namespaces';
import { User } from 'src/types/Account';
import { CreateUserSchema, UpdateUserSchema } from './account.schema';

/**
 * authenticateUserInNamespace
 *
 * @return { accountInfoWithContext  } Returns if the authentication is successful or not with the
 * account information with context and
 * If cookie auth enabled use v2 URL that sends Set-Cookie in the response headers
 * so the browser will set the cookie on subsequent calls to authendicate using cookie
 *
 */
export const authenticateUserInNamespace = (data: Partial<any>) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/login`),
    setMethod('POST'),
    setData(data),
  ).then((response) => response.data);

/**
 * Login SSO
 */
export const authenticateUserInNamespaceSSO = (data: Partial<any>) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/login/sso`),
    setMethod('POST'),
    setData(data),
  ).then((response) => response.data);

/**
 * verifyOTP
 *
 * @return { accountInfoWithContext  } Returns if the OTP is verified or not. If successful sends the logged in
 * payload response (account information with context)
 *
 */
export const verifyOTP = (data: Partial<any>) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/login/otp`),
    setMethod('POST'),
    setData(data),
  ).then((response) => response.data);

/**
 * authenticateMagicToken
 *
 * @return { accountInfo  } Returns if the authentication is successful or not with tha
 * account information
 *
 */
export const authenticateMagicToken = (data: Partial<any>) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/login/magictoken`),
    setMethod('POST'),
    setData(data),
  ).then((response) => response.data);

/**
 * createUserInNamespace
 *
 * Add a new user to your account.
 *
 * @param data { object }
 *
 * @return { accountInfoWithContext  } Returns if the authentication is successful or not with tha
 * account information with context
 *
 */
export const createUserInNamespace = (
  data: Partial<any>,
  ignoreCookie: boolean = false,
) =>
  Request<User>(
    setURL(
      `${API_ROOT}/${getNamespaceName()}/accounts${ignoreCookie ? '?login=no' : ''}`,
    ),
    setMethod('POST'),
    setData(data, CreateUserSchema),
  ).then((response) => response.data);

/**
 * getAccountByEmail
 *
 * Get exisiting user account details by email.
 *
 * @param email - email address of the user account we need to get
 *
 */
export const getAccountByEmail = (email: string) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/accounts/${email}`),
    setMethod('GET'),
  ).then((response) => response.data);

/**
 * updateUser
 *
 * Update a user's information.
 *
 * @param id { string } unique id of the user to be updated.
 * @param data { object } The fields of the user object to be updated.
 *
 */
export const updateUser = (id: string, data: Partial<any>) =>
  Request<User>(
    setURL(`${API_ROOT}/accounts/${id}`),
    setMethod('PUT'),
    setData(data, UpdateUserSchema),
  ).then((response) => response.data);

export const forgotPassword = (data: Partial<User>) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/forgot_password`),
    setMethod('POST'),
    setData(data),
  ).then((response) => response.data);

/**
 * deleteUser
 *
 * Remove a single user from your account.
 *
 * @param username { string } username of the user to be deleted.
 *
 */
export const deleteUser = (username: string) =>
  Request<{}>(
    setURL(`${API_ROOT}/account/users/${username}`),
    setMethod('DELETE'),
  ).then((response) => response.data);

/**
 * authenticateMagikToken
 *
 * @return { accountInfo  } Returns if the authentication is successful or not with tha
 * account information
 *
 */
export const createMagicLink = (data: Partial<any>) =>
  Request<User>(
    setURL(`${API_ROOT}/${getNamespaceName()}/login/magiclink`),
    setMethod('POST'),
    setData(data),
  ).then((response) => response.data);
